// Mock claim adjudication that follows the HealthRules Payer claim steps (1-28). Pure functions, no React,
// so this can move to the backend as is. Inputs: the claim, the benefit plan, the service catalog, and the
// EMR data (networks, providers, members, fee schedules, UM rules, authorizations).

import { agesOutOn, ageOn, familyMet } from "./family.js";
import { diagnosisSupports, findCriteria, icdLabel, normalizeIcd } from "./medicalNecessity.js";

export const STEP_NAMES = {
    1: "Trigger",
    2: "Match",
    3: "Verify",
    4: "Format",
    5: "Validate",
    6: "Editing",
    7: "Eligibility",
    8: "Contract",
    9: "Subcontract",
    10: "UM rules, contract",
    11: "Fee schedule",
    12: "Exclusions, contract",
    13: "Base fee",
    14: "Allowed amount",
    15: "Network tier",
    "15a": "Provider benefit network",
    "15b": "Plan benefit network",
    16: "Exclusions, benefit",
    17: "Unit limits",
    18: "Unit member responsibility threshold",
    19: "UM rules, plan",
    20: "Specific benefit provisions",
    21: "Deductibles",
    22: "Maximums",
    23: "Dollar limits",
    24: "Dollar member responsibility threshold",
    25: "Calculate cost share",
    26: "Review",
    27: "Add claim payables",
    28: "Copied to data warehouse",
};

export const LINE_STEP_IDS = ["5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "15a", "15b", "16", "17", "18", "19", "20", "21", "22", "23", "24", "25", "26"];

const SKIP = new Set(["deductible", "outOfPocketMaximum", "coinsurance", "rx", "excludedServices"]);
const SAME_DAY = {
    basiclabs: [["pcpVisit", "withPcpVisit"], ["obgynVisit", "withObgynVisit"], ["specialistVisit", "withSpecialistVisit"]],
    "basicDiagnostic/radiology": [["pcpVisit", "withPcpVisit"], ["specialistVisit", "withSpecialistVisit"]],
    basicLabImaging: [["pcpVisit", "withPcpVisit"], ["specialistVisit", "withSpecialistVisit"]],
};
const MILEAGE_NEEDS = { groundAmbulanceMileage: "groundAmbulance", airAmbulanceMileage: "airAmbulance" };
const PROTECTED_OON = new Set(["emergencyRoom", "airAmbulance", "airAmbulanceMileage"]);
const ACUPUNCTURE_ADD_ON = { "97811": ["97810"], "97814": ["97813"] };
const REFERRAL_SERVICES = new Set(["specialistVisit"]);
const DIRECT_ACCESS = new Set(["obgynVisit", "maternityProfessional"]);
const FALLBACK = {
    obgynVisit: "specialistVisit",
    maternityProfessional: "obgynVisit",
    virtualUrgentCare: "pcpVisit",
    urgentCare: "pcpVisit",
    globalSurgeryFollowUp: "pcpVisit",
    preventiveScreenings: "preventiveCare",
};
const UNIT_LIMITS = new Set(["daysPerPlanYear", "visitsPerPlanYear", "unitsPerPlanYear"]);
const DOLLAR_LIMITS = new Set(["dollarsPerPlanYear", "dollarsPerLifetime"]);
const REVIEW_DOLLARS = 10000;

const round = (value) => Math.round(value * 100) / 100;
const money = (value) => `$${Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;
const NAMES = {
    pcpVisit: "PCP visit",
    obgynVisit: "OB/GYN visit",
    basiclabs: "Basic labs",
    basicLabImaging: "Labs and imaging",
    "basicDiagnostic/radiology": "Basic radiology",
    advanceDiagnostic: "Advanced imaging",
    chiropractice: "Chiropractic",
    maternityProfessional: "Maternity, doctor",
};
const words = (key) => NAMES[key] || String(key || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (char) => char.toUpperCase())
    .replace(/(?<=\s)[A-Z](?=[a-z])/g, (char) => char.toLowerCase());
export const serviceName = (key) => words(key);
const lowerWords = (key) => words(key).replace(/^[A-Z](?=[a-z])/, (char) => char.toLowerCase());

const normalizeCode = (code) => String(code || "").trim().toUpperCase();
const normalizeRevenue = (code) => {
    const text = String(code || "").trim();
    return /^\d{1,4}$/.test(text) ? text.padStart(4, "0") : text;
};
const modifierList = (text) => String(text || "").split(/[\s,]+/).map(normalizeCode).filter(Boolean);
const inDates = (date, from, to) => !date || ((!from || date >= from) && (!to || date <= to));

const inRange = (code, from, to) => (
    code.length === String(from).length && code >= String(from).toUpperCase() && code <= String(to).toUpperCase()
);

const hasCode = (service, code, lists, groups) => {
    if (!code) return false;
    if (lists.some((field) => (service[field] || []).map(normalizeCode).includes(code))) return true;
    return groups.some((field) => (service[field] || []).some((group) => inRange(code, group.from, group.to)));
};

const matchesProcedure = (service, code) => hasCode(service, code, ["cptCodes", "hcpcsCodes"], ["cptCodeGroups", "hcpcsCodeGroups"]);
const matchesRevenue = (service, code) => hasCode(service, code, ["revenueCode"], ["revenueCodeGroups"]);

export const networkStatus = (plan, provider, data) => {
    if (!plan || !provider) return { inNetwork: false, network: null };
    const providerNetworks = (provider.networkIds || [])
        .map((id) => data.networks.find((network) => network.id === id))
        .filter((network) => network && network.kind !== "reference");
    const match = (plan.networks || [])
        .map((planNetwork) => String(planNetwork.name || "").trim().toLowerCase())
        .map((name) => providerNetworks.find((network) => network.name.trim().toLowerCase() === name))
        .find(Boolean);
    return { inNetwork: Boolean(match), network: match || null };
};

const planHas = (plan, key) => Boolean(plan.inNetwork?.[key] || plan.outOfNetwork?.[key]);

const pickService = (line, claim, provider, plan, catalog) => {
    const code = normalizeCode(line.code);
    const revenue = claim.type === "institutional" ? normalizeRevenue(line.revenueCode) : "";
    const candidates = catalog.filter((service) => matchesProcedure(service, code) || matchesRevenue(service, revenue));
    if (!candidates.length) return null;
    const scored = candidates.map((service) => {
        let score = 0;
        if (claim.type === "professional" && service.placesOfService?.length) {
            score += service.placesOfService.includes(claim.placeOfService) ? 4 : -2;
        }
        if (claim.type === "institutional" && service.typeOfBill?.length) {
            score += service.typeOfBill.includes(claim.typeOfBill) ? 3 : -1;
        }
        if (revenue && matchesRevenue(service, revenue)) score += 2;
        if (service.taxonomyCodes?.length) score += service.taxonomyCodes.includes(provider.taxonomy) ? 3 : -1;
        if (planHas(plan, service.key)) score += 2;
        return { service, score };
    });
    scored.sort((left, right) => right.score - left.score);
    return scored[0].service;
};

const costOf = (benefit, type) => (benefit?.costs || []).find((cost) => cost.type === type)?.value;

const termsText = (benefit, planCoinsurance) => {
    const copay = costOf(benefit, "copayAmount");
    const percent = costOf(benefit, "copayPercent") ?? (copay == null && benefit.deductibleApplies ? planCoinsurance : undefined);
    const parts = [];
    if (copay != null) parts.push(`${money(copay)} copay`);
    if (percent != null) parts.push(`${percent}% coinsurance`);
    if (!parts.length) parts.push("no copay or coinsurance");
    parts.push(benefit.deductibleApplies ? "after the deductible" : "the deductible doesn't apply");
    return parts.join(", ");
};

const variantFor = (benefit, claim, line) => {
    const modifiers = modifierList(line.modifiers);
    return (benefit?.variants || []).find((variant) => (
        (variant.placesOfService || []).includes(claim.placeOfService)
        || (variant.modifiers || []).some((modifier) => modifiers.includes(normalizeCode(modifier)))
    )) || null;
};

const rateFor = (schedule, line, claim) => {
    if (!schedule) return null;
    const modifiers = modifierList(line.modifiers);
    const codes = claim.type === "institutional"
        ? [normalizeRevenue(line.revenueCode), normalizeCode(line.code)]
        : [normalizeCode(line.code)];
    for (const code of codes.filter(Boolean)) {
        const withModifier = schedule.lines.find((rate) => normalizeCode(rate.code) === code && rate.modifier && modifiers.includes(normalizeCode(rate.modifier)));
        if (withModifier) return { schedule, amount: withModifier.amount, code: `${code}-${withModifier.modifier}` };
        const plain = schedule.lines.find((rate) => normalizeCode(rate.code) === code && !rate.modifier);
        if (plain) return { schedule, amount: plain.amount, code };
    }
    return null;
};

const catalogValue = (benefit, catalogService, field) => benefit?.[field] ?? catalogService?.[field];

const penaltyText = (penalty) => (penalty?.valueType === "percent" ? `${penalty.value}%` : money(penalty?.value));

// Walks the claim the way HealthRules does: claim steps 1-4, lines 5-26 one at a time, then 27-28.
export function adjudicateClaim({ claim, plan, catalog, data }) {
    const provider = data.providers.find((item) => item.id === claim.providerId) || null;
    const member = (data.members || []).find((item) => item.id === claim.memberId) || null;
    const dos = claim.dateOfService || "";
    const result = {
        claim,
        provider,
        member,
        planName: plan?.planName || "",
        status: "processed",
        network: null,
        inNetwork: false,
        steps: [],
        closingSteps: [],
        lines: [],
        payables: [],
        totals: { billed: 0, allowed: 0, planPays: 0, memberPays: 0, providerWriteOff: 0, balanceBill: 0 },
        accumulators: null,
    };
    const claimStep = (list, id, status, detail) => list.push({ id: String(id), name: STEP_NAMES[id], status, detail });
    const hold = (id, detail) => {
        claimStep(result.steps, id, "stop", detail);
        result.status = "pended";
        return result;
    };

    const lineCount = claim.lines.length;
    claimStep(result.steps, 1, "pass", `${claim.type === "institutional" ? "An" : "A"} ${claim.type} claim (${claim.type === "institutional" ? "UB-04" : "CMS-1500"}) came in with ${plural(lineCount, "line")} for ${dos || "no date of service"}.`);

    if (!plan) return hold(2, "No benefit plan is picked, so the claim can't be matched.");
    if (!provider) return hold(2, "The billing provider isn't in the provider list.");
    const family = member ? (data.families || []).find((item) => item.id === member.familyId) || null : null;
    const relationship = { subscriber: "the subscriber", spouse: "the spouse", domesticPartner: "the domestic partner", child: "a child" }[member?.relationship];
    const pcp = member?.pcpProviderId ? data.providers.find((item) => item.id === member.pcpProviderId) || null : null;
    claimStep(
        result.steps,
        2,
        member ? "pass" : "warn",
        member
            ? `Matched ${member.name} (${member.memberNumber})${family ? `, ${relationship || "a member"} in the ${family.name} (${family.familyNumber}),` : ""} on ${plan.planName}, billed by ${provider.name} (NPI ${provider.npi}).${pcp ? ` Their PCP is ${pcp.name}.` : ""}`
            : `No member is picked, so the claim matches ${plan.planName} only. Authorizations can't be matched without a member.`,
    );

    const missing = [];
    if (!dos) missing.push("a date of service");
    if (!/^\d{10}$/.test(String(provider.npi || ""))) missing.push("a 10-digit NPI");
    if (!lineCount) missing.push("at least one line");
    if (!(claim.diagnosisCodes || []).length) missing.push("at least one diagnosis code");
    if (claim.type === "institutional" && !claim.typeOfBill) missing.push("a type of bill");
    if (claim.type === "professional" && !claim.placeOfService) missing.push("a place of service");
    if (missing.length) return hold(3, `The claim needs ${missing.join(", ")}. It goes back for repair.`);
    claimStep(result.steps, 3, "pass", "The claim has every required field. The claim data is locked for processing.");

    const cleaned = [];
    const diagnosisCodes = (claim.diagnosisCodes || []).map((code) => {
        const clean = normalizeIcd(code);
        if (clean !== String(code).trim()) cleaned.push(`diagnosis ${code} to ${clean}`);
        return clean;
    });
    claim.lines.forEach((line, index) => {
        const code = normalizeCode(line.code);
        if (line.code && code !== String(line.code).trim()) cleaned.push(`line ${index + 1} code ${line.code} to ${code}`);
        const revenue = normalizeRevenue(line.revenueCode);
        if (claim.type === "institutional" && line.revenueCode && revenue !== String(line.revenueCode).trim()) cleaned.push(`line ${index + 1} revenue ${line.revenueCode} to ${revenue}`);
    });
    claimStep(result.steps, 4, "pass", cleaned.length ? `Put codes in standard form: ${cleaned.join("; ")}.` : "Codes are already in standard form.");

    const status = networkStatus(plan, provider, data);
    result.inNetwork = status.inNetwork;
    result.network = status.network;
    const referenceNetwork = data.networks.find((network) => network.kind === "reference");
    const contractNetworkId = status.inNetwork ? status.network.id : referenceNetwork?.id;
    const schedule = data.feeSchedules.find((item) => item.networkId === contractNetworkId && !item.providerId) || null;
    const subSchedule = data.feeSchedules.find((item) => item.networkId === contractNetworkId && item.providerId === provider.id) || null;
    const providerNetworkNames = (provider.networkIds || [])
        .map((id) => data.networks.find((network) => network.id === id)?.name)
        .filter(Boolean);
    const planNetworkNames = (plan.networks || []).map((network) => network.name).filter(Boolean);

    const matched = claim.lines.map((line) => pickService(line, claim, provider, plan, catalog));
    const keysOnClaim = new Set(matched.filter(Boolean).map((service) => service.key));
    const codesOnClaim = new Set(claim.lines.map((line) => normalizeCode(line.code)));
    const planCoinsuranceFor = (side) => plan[side]?.coinsurance?.value;

    const sideName = (key) => {
        if (status.inNetwork) return "inNetwork";
        if (!PROTECTED_OON.has(key)) return "outOfNetwork";
        if (key === "emergencyRoom") return claim.emergency ? "inNetwork" : "outOfNetwork";
        return "inNetwork";
    };
    const accum = {};
    const accumFor = (side) => {
        if (!accum[side]) {
            const deductible = Number(plan[side]?.deductible?.individual || 0);
            const outOfPocket = Number(plan[side]?.outOfPocketMaximum?.individual || 0);
            const deductibleMet = side === "inNetwork" ? Number(claim.deductibleMet || 0) : Number(claim.oonDeductibleMet || 0);
            const outOfPocketMet = side === "inNetwork" ? Number(claim.outOfPocketMet || 0) : Number(claim.oonOutOfPocketMet || 0);
            const others = member ? familyMet(data, member, dos, side) : { others: [], deductible: 0, outOfPocket: 0 };
            const familyTier = others.others.length > 0;
            const familyDeductible = familyTier ? Number(plan[side]?.deductible?.family || 0) : 0;
            const familyOutOfPocket = familyTier ? Number(plan[side]?.outOfPocketMaximum?.family || 0) : 0;
            const deductibleLeft = Math.max(0, deductible - deductibleMet);
            const familyDeductibleLeft = familyDeductible > 0 ? Math.max(0, familyDeductible - deductibleMet - others.deductible) : Infinity;
            const outOfPocketLeft = outOfPocket > 0 ? Math.max(0, outOfPocket - outOfPocketMet) : Infinity;
            const familyOutOfPocketLeft = familyOutOfPocket > 0 ? Math.max(0, familyOutOfPocket - outOfPocketMet - others.outOfPocket) : Infinity;
            accum[side] = {
                deductible,
                outOfPocket,
                familyDeductible,
                familyOutOfPocket,
                familyDeductibleMet: round(deductibleMet + others.deductible),
                familyOutOfPocketMet: round(outOfPocketMet + others.outOfPocket),
                deductibleBy: familyDeductibleLeft < deductibleLeft ? "family" : "individual",
                outOfPocketBy: familyOutOfPocketLeft < outOfPocketLeft ? "family" : "individual",
                deductibleLeft: Math.min(deductibleLeft, familyDeductibleLeft),
                outOfPocketLeft: Math.min(outOfPocketLeft, familyOutOfPocketLeft),
                deductibleApplied: 0,
                outOfPocketApplied: 0,
            };
        }
        return accum[side];
    };
    const copayTaken = new Set();
    const authUsed = {};
    const unitsOnClaim = {};
    const dollarsOnClaim = {};

    const findAuth = (type, serviceKey, extra = () => true) => {
        if (!member) return null;
        const candidates = (data.authorizations || [])
            .filter((auth) => (auth.type || "authorization") === type && auth.serviceKey === serviceKey && auth.memberId === member.id)
            .filter((auth) => inDates(dos, auth.from, auth.to))
            .filter((auth) => !auth.providerId || auth.providerId === provider.id)
            .filter(extra)
            .sort((left, right) => Number(Boolean(right.providerId)) - Number(Boolean(left.providerId)));
        const left = (auth) => Number(auth.units || 0) - Number(auth.used || 0) - (authUsed[auth.id] || 0);
        return candidates.find((auth) => auth.status === "approved" && left(auth) > 0)
            || candidates.find((auth) => auth.status === "approved")
            || candidates.find((auth) => auth.status === "denied")
            || candidates.find((auth) => auth.status === "pended")
            || null;
    };
    const unitsLeftOn = (auth) => Number(auth.units || 0) - Number(auth.used || 0) - (authUsed[auth.id] || 0);

    claim.lines.forEach((line, index) => {
        const billed = round(Number(line.billed || 0));
        const units = Math.max(1, Number(line.units || 1));
        const out = {
            index,
            code: normalizeCode(line.code),
            revenueCode: claim.type === "institutional" ? normalizeRevenue(line.revenueCode) : "",
            modifiers: line.modifiers || "",
            units,
            paidUnits: units,
            billed,
            serviceKey: "",
            service: "",
            benefit: "",
            status: "paid",
            allowed: 0,
            copay: 0,
            deductible: 0,
            coinsurance: 0,
            penalty: 0,
            providerWriteOff: 0,
            overLimit: 0,
            memberPays: 0,
            planPays: 0,
            balanceBill: 0,
            authorization: "",
            notes: [],
            steps: [],
        };
        const add = (id, stepStatus, detail) => out.steps.push({ id: String(id), name: STEP_NAMES[id], status: stepStatus, detail });
        let stoppedAt = null;
        const stop = (id, outcome, reason) => {
            out.status = outcome;
            out.notes.push(reason);
            add(id, "stop", reason);
            stoppedAt = String(id);
            if (outcome === "denied") {
                out.allowed = 0;
                out.balanceBill = status.inNetwork ? 0 : billed;
            }
            return true;
        };
        const penalties = [];
        let paidUnits = units;
        let billedForPaidUnits = billed;
        let allowed = 0;
        let agreement = null;
        const codeText = [out.revenueCode, out.code].filter(Boolean).join(" / ");

        const applyUm = (id, rule, key) => {
            const type = rule.type || "authorization";
            const noun = type === "referral" ? "referral" : "approval";
            const auth = findAuth(type, key);
            if (auth?.status === "approved" && unitsLeftOn(auth) > 0) {
                const take = Math.min(unitsLeftOn(auth), paidUnits);
                authUsed[auth.id] = (authUsed[auth.id] || 0) + take;
                out.authorization = auth.number;
                if (take < paidUnits) {
                    const extra = paidUnits - take;
                    paidUnits = take;
                    add(id, "warn", `${rule.name}. ${auth.number} has ${plural(take, "unit")} left, so ${plural(extra, "unit")} on this line ${extra === 1 ? "is" : "are"} denied.`);
                } else {
                    add(id, "pass", `${rule.name}. Matched ${auth.number}, with ${unitsLeftOn(auth)} of ${auth.units} left after this line.`);
                }
                return false;
            }
            if (auth?.status === "denied") return stop(id, "denied", `${rule.name}. ${auth.number} was denied.`);
            if (auth?.status === "pended") return stop(id, "pended", `${rule.name}. ${auth.number} is still pending.`);
            const why = !member
                ? `No member is picked to match ${noun === "approval" ? "an approval" : "a referral"}`
                : auth
                    ? `${auth.number} has no units left`
                    : `No ${noun} is on file`;
            if (rule.missingAuth === "deny") return stop(id, "denied", `${rule.name}. ${why}. The line is denied.`);
            if (rule.missingAuth === "pend" || !rule.penalty?.value) return stop(id, "pended", `${rule.name}. ${why}. The line is held for review.`);
            penalties.push({ payer: rule.payer, valueType: rule.penalty.valueType, value: Number(rule.penalty.value), reason: rule.name });
            add(id, "warn", `${rule.name}. ${why}. The ${rule.payer === "provider" ? "provider" : "member"} takes a ${penaltyText(rule.penalty)} penalty.`);
            return false;
        };
        const umApplies = (rule) => rule.when !== "notEmergency" || !claim.emergency;

        const run = () => {
            if (!out.code && !out.revenueCode) return stop(5, "pended", "The line has no procedure or revenue code. It goes back for repair.");
            if (billed <= 0) return stop(5, "pended", "The line has no billed amount. It goes back for repair.");
            const service = matched[index];
            if (!service) return stop(5, "denied", `No service in the catalog uses ${codeText}.`);
            let key = service.key;
            let catalogService = service;
            out.serviceKey = key;
            out.service = words(key);
            add(5, "pass", `${codeText} is a valid code for ${words(key)}.`);

            const edits = [];
            if (catalogService.taxonomyCodes?.length && !catalogService.taxonomyCodes.includes(provider.taxonomy)) {
                const action = plan.inNetwork?.[key]?.taxonomyMismatch || catalogService.taxonomyMismatch || "continue";
                const reroute = { routeToSpecialist: "specialistVisit", routeToSickVisit: "pcpVisit" }[action];
                if (action === "deny") return stop(6, "denied", `${provider.specialty || "This provider type"} can't bill ${lowerWords(key)}.`);
                if (action === "pendForCredentialReview") return stop(6, "pended", `Hold for a license check. ${provider.specialty || "This provider type"} isn't on the ${lowerWords(key)} list.`);
                if (reroute && reroute !== key) {
                    edits.push(`${provider.specialty || "This provider"} isn't on the ${lowerWords(key)} list, so it pays as a ${lowerWords(reroute)}.`);
                    key = reroute;
                    catalogService = catalog.find((item) => item.key === reroute) || catalogService;
                    out.serviceKey = key;
                    out.service = words(key);
                }
            }
            if (MILEAGE_NEEDS[key] && !keysOnClaim.has(MILEAGE_NEEDS[key])) return stop(6, "denied", `Miles only pay with a ${lowerWords(MILEAGE_NEEDS[key])} ride on the same claim.`);
            if (ACUPUNCTURE_ADD_ON[out.code] && !ACUPUNCTURE_ADD_ON[out.code].some((code) => codesOnClaim.has(code))) return stop(6, "denied", "Extra acupuncture time only pays with the first 15 minutes on the same day.");
            if (key === "globalSurgeryFollowUp") return stop(6, "denied", "The surgery fee already covers this follow-up visit, so it is bundled.");
            const necessity = findCriteria(data.medicalNecessity, key, out.code);
            if (necessity && !diagnosisSupports(necessity, diagnosisCodes)) {
                return stop(6, "denied", `Not medically necessary. ${necessity.name} needs a diagnosis like ${necessity.diagnoses.slice(0, 2).map(icdLabel).join(" or ")}, and the claim has ${diagnosisCodes.join(", ")}.`);
            }
            const supportNote = necessity?.diagnoses.length
                ? ` ${icdLabel(diagnosisCodes.find((code) => necessity.diagnoses.includes(code)))} supports ${necessity.name}.`
                : "";
            const maxUnits = catalogValue(plan.inNetwork?.[key], catalogService, "maxDailyUnits");
            if (maxUnits && units > maxUnits) {
                paidUnits = maxUnits;
                edits.push(`Only ${maxUnits} per day can pay, so ${plural(units - maxUnits, "unit")} ${units - maxUnits === 1 ? "is" : "are"} denied.`);
            }
            add(6, edits.length ? "warn" : "pass", `${edits.length ? edits.join(" ") : "No coding edits apply."}${supportNote}`);

            if (!inDates(dos, plan.planEffectiveDate, plan.planTerminationDate)) {
                return stop(7, "denied", `${plan.planName} isn't in effect on ${dos}. It runs ${plan.planEffectiveDate || "?"} to ${plan.planTerminationDate || "?"}.`);
            }
            if (member && !inDates(dos, member.coverageFrom, member.coverageTo)) {
                return stop(7, "denied", `${member.name} is covered ${member.coverageFrom || "?"} to ${member.coverageTo || "?"}, not on ${dos}.`);
            }
            const ageLimit = agesOutOn(member);
            if (ageLimit && dos > ageLimit) {
                return stop(7, "denied", `${member.name} is ${ageOn(member.dateOfBirth, dos)} and aged out as a dependent child on ${ageLimit}, the end of the month they turned 26.`);
            }
            add(7, member ? "pass" : "warn", member
                ? `${member.name} is covered on ${dos}.${member.relationship === "child" ? ` As a child, they stay covered through ${ageLimit}.` : ""}`
                : `No member is picked. ${plan.planName} is in effect on ${dos}.`);

            add(8, status.inNetwork ? "pass" : "note", status.inNetwork
                ? `${provider.name} has a ${status.network.name} contract, and ${plan.planName} uses ${status.network.name}.`
                : `${provider.name} has no contract with ${plan.planName}'s networks, so out-of-network pricing applies.`);
            add(9, subSchedule ? "note" : "pass", subSchedule ? `${subSchedule.name} has rates for this provider that go before the network rates.` : "This provider has no rates of its own.");

            const contractRules = status.inNetwork
                ? (data.umRules || []).filter((rule) => rule.source === "contract" && rule.serviceKey === key && rule.networkId === status.network.id && umApplies(rule))
                : [];
            if (!contractRules.length) add(10, "pass", "No contract rule needs approval for this service.");
            for (const rule of contractRules) {
                if (applyUm(10, { ...rule, payer: "provider" }, key)) return true;
            }

            const rate = rateFor(subSchedule, line, claim) || rateFor(schedule, line, claim);
            const percent = Number(schedule?.fallbackPercentOfBilled || 100);
            add(11, rate ? "pass" : "warn", rate
                ? `${rate.schedule.name} lists ${rate.code} at ${money(rate.amount)} a unit.`
                : `${schedule?.name || "No fee schedule"} has no price for ${codeText}, so it pays ${percent}% of billed.`);

            const excludedBy = [subSchedule, schedule].find((item) => item && (item.excludedCodes || []).map(normalizeCode).some((code) => code && (code === out.code || code === out.revenueCode)));
            if (excludedBy) return stop(12, "denied", `The ${excludedBy.name} contract doesn't pay ${codeText}.`);
            add(12, "pass", "The contract doesn't exclude this code.");

            billedForPaidUnits = round(billed * (paidUnits / units));
            const base = rate ? round(rate.amount * paidUnits) : round(billedForPaidUnits * percent / 100);
            add(13, "pass", rate
                ? `${money(rate.amount)} × ${paidUnits} = ${money(base)}.`
                : `${percent}% of ${money(billedForPaidUnits)} billed = ${money(base)}.`);

            agreement = findAuth("authorization", key, (auth) => auth.status === "approved" && auth.agreedAmount != null);
            if (agreement) {
                allowed = round(Number(agreement.agreedAmount));
                out.authorization = agreement.number;
                add(14, "note", `The agreement on ${agreement.number} replaces the fee. Allowed is ${money(allowed)}.`);
            } else {
                allowed = round(Math.min(base, billedForPaidUnits));
                add(14, "pass", allowed < base
                    ? `Billed (${money(billedForPaidUnits)}) is less than the fee, so allowed is the billed amount.`
                    : `Allowed is ${money(allowed)}, the lower of the fee and the billed amount.`);
            }

            const side = sideName(key);
            const protectedOon = !status.inNetwork && side === "inNetwork";
            out.protectedOon = protectedOon;
            add(15, status.inNetwork ? "pass" : protectedOon ? "note" : "warn", status.inNetwork
                ? "In-network tier. The provider is par."
                : protectedOon
                    ? "The provider is non-par, but federal law protects this service, so it pays at the in-network tier."
                    : "Out-of-network tier. The provider is non-par.");
            add("15a", "note", providerNetworkNames.length ? `${provider.name} is in ${providerNetworkNames.join(", ")}.` : `${provider.name} is in no networks.`);
            add("15b", status.inNetwork ? "pass" : "note", `${plan.planName} uses ${planNetworkNames.join(", ") || "no networks"}. ${status.inNetwork ? `Same network: ${status.network.name}.` : "No network in common."}`);

            const provisionNotes = [];
            const excluded = plan.inNetwork?.excludedServices?.[key] || plan[side]?.excludedServices?.[key];
            if (excluded && excluded.isCovered === false) return stop(16, "denied", `${words(key)} is excluded on ${plan.planName}.`);
            const fallbackKey = FALLBACK[key];
            if (!plan[side]?.[key] && fallbackKey && plan[side]?.[fallbackKey]) {
                provisionNotes.push(`${plan.planName} has no ${lowerWords(key)} line, so it pays under the ${lowerWords(fallbackKey)} benefit.`);
                key = fallbackKey;
                catalogService = catalog.find((item) => item.key === fallbackKey) || catalogService;
                out.serviceKey = key;
                out.service = words(key);
            }
            const planCoinsurance = planCoinsuranceFor(side);
            let benefit = plan[side]?.[key];
            let label = words(key);
            if (!benefit) {
                const hasAnyOon = Object.keys(plan.outOfNetwork || {}).some((field) => !SKIP.has(field));
                if (side === "outOfNetwork" && !hasAnyOon) return stop(16, "denied", `${plan.planName} has no out-of-network benefit.`);
                if (planCoinsurance == null) return stop(16, "denied", `${words(key)} has no benefit on ${plan.planName}.`);
                benefit = { isCovered: true, deductibleApplies: true, costs: [{ type: "copayPercent", value: planCoinsurance }] };
                label = `${words(key)} (plan coinsurance)`;
                provisionNotes.push(`${plan.planName} has no line for this service, so it pays at the plan's deductible and coinsurance.`);
            }
            if (benefit.isCovered === false) return stop(16, "denied", `${words(key)} isn't covered ${side === "inNetwork" ? "in network" : "out of network"}.`);
            add(16, "pass", excluded ? `${words(key)} is on the exclusion list but covered: ${(excluded.conditions || []).join(" ") || "with conditions"}.` : "The benefit isn't excluded.");

            const limitRules = (plan[side]?.[key]?.limits || []).filter((limit) => UNIT_LIMITS.has(limit.type));
            if (!limitRules.length) add(17, "pass", "No unit limit on this service.");
            for (const limit of limitRules) {
                const shared = [key, ...(limit.sharedWith || [])];
                const used = shared.reduce((sum, item) => sum + Number(member?.unitsUsed?.[item] || 0) + (unitsOnClaim[item] || 0), 0);
                const left = Number(limit.value) - used;
                const sharedText = limit.sharedWith?.length ? `, shared with ${limit.sharedWith.map(lowerWords).join(" and ")}` : "";
                if (left <= 0) return stop(17, "denied", `The ${limit.value}-a-year limit${sharedText} is used up (${used} used).`);
                if (left < paidUnits) {
                    const scale = left / paidUnits;
                    allowed = round(allowed * scale);
                    billedForPaidUnits = round(billedForPaidUnits * scale);
                    add(17, "warn", `Only ${plural(left, "unit")} left of ${limit.value} a year${sharedText}. ${plural(paidUnits - left, "unit")} denied.`);
                    paidUnits = left;
                } else {
                    add(17, "pass", `${used} of ${limit.value} used this year${sharedText}. ${left - paidUnits} left after this line.`);
                }
            }
            unitsOnClaim[key] = (unitsOnClaim[key] || 0) + paidUnits;

            const threshold = plan[side]?.[key]?.memberResponsibilityThreshold;
            let waive = false;
            if (threshold?.type === "units") {
                const used = Number(member?.unitsUsed?.[key] || 0) + (unitsOnClaim[key] || 0) - paidUnits;
                waive = used < Number(threshold.value);
                add(18, waive ? "note" : "pass", waive
                    ? `Visit ${used + 1} of the first ${threshold.value} the plan pays in full. The member pays nothing on this line.`
                    : `The first ${threshold.value} visits were paid in full. Regular cost share applies now.`);
            } else {
                add(18, "pass", "No unit threshold on this service.");
            }

            const planRules = [];
            if (REFERRAL_SERVICES.has(key) && !DIRECT_ACCESS.has(service.key) && plan.referralRequired) {
                const referralPenalty = plan[side]?.[key]?.noReferralPenalty;
                planRules.push({ name: "Specialists need a PCP referral", type: "referral", missingAuth: referralPenalty?.value ? "penalty" : "deny", penalty: referralPenalty, payer: "member" });
            }
            const precert = plan[side]?.[key]?.precertification;
            if (precert?.required) {
                planRules.push({ name: `${plan.planName} needs approval first`, type: "authorization", missingAuth: precert.penalty?.value ? "penalty" : "pend", penalty: precert.penalty, payer: precert.responsibility === "provider" ? "provider" : "member" });
            }
            (data.umRules || [])
                .filter((rule) => rule.source === "plan" && rule.serviceKey === key && umApplies(rule))
                .forEach((rule) => planRules.push({ ...rule, payer: "member" }));
            const pcpNote = !plan.referralRequired || !member || key !== "pcpVisit"
                ? ""
                : !pcp
                    ? `${member.name} has no PCP on file, and ${plan.planName} asks every member to pick one. The visit still pays.`
                    : pcp.id !== provider.id
                        ? `${provider.name} isn't ${member.name}'s PCP (${pcp.name}). The visit still pays, and referrals have to come from ${pcp.name}.`
                        : "";
            if (pcpNote) add(19, "warn", pcpNote);
            if (!planRules.length && !pcpNote) {
                add(19, "pass", key === "pcpVisit" && pcp?.id === provider.id
                    ? `${provider.name} is ${member.name}'s PCP. No plan rule needs approval or a referral for this service.`
                    : "No plan rule needs approval or a referral for this service.");
            }
            for (const rule of planRules) {
                const before = paidUnits;
                if (applyUm(19, rule, key)) return true;
                if (paidUnits < before) {
                    const scale = paidUnits / before;
                    allowed = round(allowed * scale);
                    billedForPaidUnits = round(billedForPaidUnits * scale);
                    unitsOnClaim[key] -= before - paidUnits;
                }
            }

            const sameDay = (SAME_DAY[key] || []).find(([visitKey, field]) => keysOnClaim.has(visitKey) && benefit[field]);
            if (sameDay) {
                benefit = { ...benefit[sameDay[1]], countsTowardOutOfPocket: benefit.countsTowardOutOfPocket };
                label = `${words(key)}, with ${lowerWords(sameDay[0])}`;
                provisionNotes.push(`Done the same day as a ${lowerWords(sameDay[0])}, so the same-day amount applies.`);
            }
            if (key === "emergencyRoom") {
                const professional = claim.type === "professional";
                const nonEmergentBenefit = professional ? benefit.professionalNonEmergent : benefit.nonEmergentFacility;
                if (claim.emergency) {
                    benefit = professional ? benefit.professional || benefit : benefit;
                    label = professional ? "Emergency room, doctor" : "Emergency room, facility";
                } else {
                    const erPenalty = plan[side]?.emergencyRoom?.nonEmergentPenalty;
                    if (nonEmergentBenefit) benefit = nonEmergentBenefit;
                    label = professional ? "Emergency room, doctor, not an emergency" : "Emergency room, facility, not an emergency";
                    if (erPenalty?.value && !professional) {
                        penalties.push({ payer: "member", valueType: erPenalty.valueType, value: Number(erPenalty.value), reason: "Not an emergency" });
                        provisionNotes.push(`The visit wasn't an emergency, so a ${penaltyText(erPenalty)} penalty applies.`);
                    }
                }
                if (claim.admitted) {
                    benefit = { ...benefit, costs: (benefit.costs || []).filter((cost) => cost.type !== "copayAmount") };
                    provisionNotes.push("The member was admitted, so the ER copay is waived.");
                }
            }
            const variant = variantFor(plan[side]?.[key], claim, line);
            if (variant) {
                benefit = variant;
                label = `${label}, ${[...(variant.placesOfService || []), ...(variant.modifiers || [])].join(" / ")}`;
                provisionNotes.push("A place of service or modifier line on the plan applies.");
            }
            out.benefit = label;
            add(20, "pass", `${label}: ${termsText(benefit, planCoinsurance)}.${provisionNotes.length ? ` ${provisionNotes.join(" ")}` : ""}`);

            const accumulators = accumFor(side);
            let left = allowed;
            const copay = waive ? null : costOf(benefit, "copayAmount");
            const copayKey = `${side}-${key}`;
            let copayNote = "";
            if (copay != null && !copayTaken.has(copayKey)) {
                out.copay = round(Math.min(copay, left));
                left = round(left - out.copay);
                copayTaken.add(copayKey);
            } else if (copay) {
                copayNote = " The copay was already taken on an earlier line of this visit.";
            }
            if (benefit.deductibleApplies && !waive) {
                out.deductible = round(Math.min(left, accumulators.deductibleLeft));
                left = round(left - out.deductible);
            }
            const coinsurancePercent = waive ? null : costOf(benefit, "copayPercent") ?? (copay == null && benefit.deductibleApplies ? planCoinsurance : undefined);
            if (coinsurancePercent != null) out.coinsurance = round(left * coinsurancePercent / 100);
            add(21, benefit.deductibleApplies && !waive ? "pass" : "note", !benefit.deductibleApplies || waive
                ? "The deductible doesn't apply to this benefit."
                : accumulators.deductible === 0
                    ? "This tier has no deductible."
                    : accumulators.deductibleBy === "family"
                        ? `${money(out.deductible)} goes to the deductible. The family has met ${money(accumulators.familyDeductibleMet)} of its ${money(accumulators.familyDeductible)} deductible, so ${money(accumulators.deductibleLeft - out.deductible)} is left for everyone.`
                        : `${money(out.deductible)} goes to the deductible. ${money(accumulators.deductibleLeft - out.deductible)} left of ${money(accumulators.deductible)}.`);

            let costShare = round(out.copay + out.deductible + out.coinsurance);
            const counts = catalogValue(plan[side]?.[key], catalogService, "countsTowardOutOfPocket") !== false;
            let maxNote;
            if (!counts) {
                maxNote = "This service doesn't count toward the out-of-pocket maximum.";
            } else {
                let capped = false;
                if (costShare > accumulators.outOfPocketLeft) {
                    capped = true;
                    let room = accumulators.outOfPocketLeft;
                    ["copay", "deductible", "coinsurance"].forEach((field) => {
                        out[field] = round(Math.min(out[field], room));
                        room = round(room - out[field]);
                    });
                    costShare = round(out.copay + out.deductible + out.coinsurance);
                }
                if (Number.isFinite(accumulators.outOfPocketLeft)) accumulators.outOfPocketLeft = round(accumulators.outOfPocketLeft - costShare);
                accumulators.outOfPocketApplied = round(accumulators.outOfPocketApplied + costShare);
                maxNote = !Number.isFinite(accumulators.outOfPocketLeft)
                    ? `${money(costShare)} counts toward the out-of-pocket maximum. This tier has no maximum.`
                    : accumulators.outOfPocketBy === "family"
                        ? `${money(costShare)} counts toward the out-of-pocket maximum. ${money(accumulators.outOfPocketLeft)} left of the ${money(accumulators.familyOutOfPocket)} family maximum.${capped ? " The family hit the maximum, so the plan pays the rest." : ""}`
                        : `${money(costShare)} counts toward the out-of-pocket maximum. ${money(accumulators.outOfPocketLeft)} left of ${money(accumulators.outOfPocket)}.${capped ? " The member hit the maximum, so the plan pays the rest." : ""}`;
            }
            accumulators.deductibleLeft = round(accumulators.deductibleLeft - out.deductible);
            accumulators.deductibleApplied = round(accumulators.deductibleApplied + out.deductible);
            add(22, "pass", maxNote);

            const penaltyAmount = (item) => round(item.valueType === "percent" ? allowed * item.value / 100 : item.value);
            out.penalty = round(penalties.filter((item) => item.payer !== "provider").reduce((sum, item) => sum + penaltyAmount(item), 0));
            out.providerWriteOff = round(Math.min(allowed - costShare, penalties.filter((item) => item.payer === "provider").reduce((sum, item) => sum + penaltyAmount(item), 0)));
            let planPays = round(Math.max(0, allowed - costShare - out.providerWriteOff));

            const dollarLimits = (plan[side]?.[key]?.limits || []).filter((limit) => DOLLAR_LIMITS.has(limit.type));
            if (!dollarLimits.length) add(23, "pass", "No dollar limit on this service.");
            for (const limit of dollarLimits) {
                const used = Number(member?.dollarsUsed?.[key] || 0) + (dollarsOnClaim[key] || 0);
                const room = Math.max(0, Number(limit.value) - used);
                const period = limit.type === "dollarsPerLifetime" ? "lifetime" : "yearly";
                if (planPays > room) {
                    out.overLimit = round(planPays - room);
                    planPays = round(room);
                    add(23, "warn", `The ${money(limit.value)} ${period} limit caps what the plan pays. The member owes the ${money(out.overLimit)} over the limit.`);
                } else {
                    add(23, "pass", `${money(used + planPays)} of the ${money(limit.value)} ${period} limit used after this line.`);
                }
            }
            dollarsOnClaim[key] = round((dollarsOnClaim[key] || 0) + planPays);

            const dollarThreshold = plan[side]?.[key]?.memberResponsibilityThreshold;
            add(24, "pass", dollarThreshold?.type === "dollars" ? "A dollar threshold is set, but the mock doesn't apply it yet." : "No dollar threshold on this service.");

            out.allowed = allowed;
            out.paidUnits = paidUnits;
            out.planPays = planPays;
            out.memberPays = round(costShare + out.penalty + out.overLimit);
            if (!status.inNetwork && !protectedOon && !agreement) out.balanceBill = round(Math.max(0, billedForPaidUnits - allowed));
            const parts = [
                out.copay ? `${money(out.copay)} copay` : "",
                out.deductible ? `${money(out.deductible)} deductible` : "",
                out.coinsurance ? `${money(out.coinsurance)} coinsurance` : "",
                out.penalty ? `${money(out.penalty)} penalty` : "",
                out.overLimit ? `${money(out.overLimit)} over the limit` : "",
            ].filter(Boolean);
            add(25, "pass", [
                `Member pays ${money(out.memberPays)}${parts.length ? ` (${parts.join(", ")})` : ""}. Plan pays ${money(planPays)}.${copayNote}`,
                out.providerWriteOff ? `The provider writes off ${money(out.providerWriteOff)} for the missing approval.` : "",
                out.balanceBill ? `The provider is out of network and may bill the member the ${money(out.balanceBill)} above the allowed amount.` : "",
                protectedOon ? "The member can't be billed above the allowed amount." : "",
                agreement && !status.inNetwork ? `The provider agreed to ${agreement.number}, so it can't bill the member above the allowed amount.` : "",
            ].filter(Boolean).join(" "));
            return false;
        };

        run();
        if (stoppedAt) {
            LINE_STEP_IDS
                .filter((id) => id !== "26" && !out.steps.some((item) => item.id === id))
                .forEach((id) => add(id, "skip", `Not run. The line stopped at step ${stoppedAt}.`));
            out.steps.sort((left, right) => LINE_STEP_IDS.indexOf(left.id) - LINE_STEP_IDS.indexOf(right.id));
        }
        if (out.status === "pended") add(26, "warn", "Sent to the review queue before it can pay.");
        else if (billed >= REVIEW_DOLLARS) add(26, "warn", `The line is billed at ${money(billed)}, so it gets a second look before payment.`);
        else if (out.status === "denied") add(26, "note", "Denied. The reason prints on the remittance.");
        else add(26, "pass", "Paid automatically. No review needed.");

        result.lines.push(out);
        result.totals.billed += out.billed;
        result.totals.allowed += out.allowed;
        result.totals.planPays += out.planPays;
        result.totals.memberPays += out.memberPays;
        result.totals.providerWriteOff += out.providerWriteOff;
        result.totals.balanceBill += out.balanceBill;
    });

    Object.keys(result.totals).forEach((field) => {
        result.totals[field] = round(result.totals[field]);
    });
    const pendedLines = result.lines.filter((line) => line.status === "pended").length;
    if (pendedLines) result.status = "pended";
    if (result.totals.planPays > 0) {
        result.payables.push({ payee: provider.name, npi: provider.npi, amount: result.totals.planPays });
    }
    claimStep(
        result.closingSteps,
        27,
        result.totals.planPays > 0 ? "pass" : "note",
        [
            result.totals.planPays > 0 ? `Pay ${provider.name} ${money(result.totals.planPays)}.` : "Nothing to pay the provider.",
            `The member owes ${money(result.totals.memberPays)}.`,
            pendedLines ? `${plural(pendedLines, "line")} on hold won't pay until review.` : "",
        ].filter(Boolean).join(" "),
    );
    claimStep(result.closingSteps, 28, "note", "Mock only: the result is logged to the browser console and nothing is saved. The claim data is unlocked, and the next claim can start.");

    result.accumulators = Object.fromEntries(Object.entries(accum).map(([side, value]) => [side, {
        deductible: value.deductible,
        deductibleApplied: value.deductibleApplied,
        deductibleLeft: value.deductibleLeft,
        outOfPocket: value.outOfPocket,
        outOfPocketApplied: value.outOfPocketApplied,
        outOfPocketLeft: Number.isFinite(value.outOfPocketLeft) ? value.outOfPocketLeft : null,
        familyDeductible: value.familyDeductible,
        familyDeductibleMet: round(value.familyDeductibleMet + value.deductibleApplied),
        familyOutOfPocket: value.familyOutOfPocket,
        familyOutOfPocketMet: round(value.familyOutOfPocketMet + value.outOfPocketApplied),
        deductibleBy: value.deductibleBy,
        outOfPocketBy: value.outOfPocketBy,
    }]));
    return result;
}
